<script setup lang="ts">
import { useEventsApi } from '~/composables/api/use-events-api';
import { useMyInterests } from '~/stores/use-my-interests';

useHead(
  {
    title: 'Browse Events | Arvee Events',
    meta: [
      {
        name: 'description',
        content:
          'Discover community-driven RV events with threshold-based funding, plus third-party rallies, expos, and gatherings from across the community. Browse upcoming events.',
      },
      {
        property: 'og:title',
        content: 'Browse Events | Arvee Events',
      },
      {
        property: 'og:description',
        content:
          'Discover community-driven RV events with threshold-based funding, plus third-party rallies, expos, and gatherings from across the community. Browse upcoming events.',
      },
      { property: 'og:image', content: 'https://events.arvee.io/og-image.jpg' },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: 'https://events.arvee.io/browse' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    link: [{ rel: 'canonical', href: 'https://events.arvee.io/browse' }],
  },
  { tagPriority: 10 },
);

const config = useRuntimeConfig();
const route = useRoute();

const statusOptions = [
  { label: 'All', value: undefined },
  { label: 'Fundraising', value: 'fundraising' },
  { label: 'Confirmed', value: 'confirmed' },
] as const;

const activeStatus = computed(
  () => (route.query.status as string | undefined) ?? undefined,
);

const currentPage = computed(() => {
  const p = Number(route.query.page);
  return p > 0 ? p : 1;
});

const { data, status, refresh } = useAsyncData(
  'browse-events',
  () => {
    const params: Record<string, unknown> = {};
    if (activeStatus.value) params.status = activeStatus.value;
    if (currentPage.value > 1) params.page = currentPage.value;
    return useEventsApi().browse(params);
  },
  {
    // Watch only the community section's own inputs so toggling the discover
    // section's filters on this shared page doesn't refetch community events.
    watch: [activeStatus, currentPage],
    server: !config.public.mock,
  },
);

function setFilter(value: string | undefined) {
  navigateTo({
    query: {
      ...route.query,
      status: value,
      page: undefined,
    },
  });
}

const searchInput = ref('');

function goToSearch() {
  const q = searchInput.value.trim();
  if (q.length >= 2) {
    navigateTo({ path: '/search', query: { q } });
  }
}

function setPage(page: number) {
  navigateTo({
    query: {
      ...route.query,
      page: page > 1 ? page : undefined,
    },
  });
}

// Warm the shared interest store so the community cards' interest toggles seed
// their current rung once auth settles. Client + authed only (the store bails
// otherwise); the store dedupes across the page's surfaces.
onMounted(() => {
  useMyInterests().ensureUpcoming();
});
</script>

<template>
  <div>
    <h1 class="text-h4 mb-6">Events</h1>

    <!-- Community events section -->
    <h2 class="text-h5 mb-4">Community events</h2>

    <!-- Search -->
    <v-text-field
      v-model="searchInput"
      aria-label="Search events"
      prepend-inner-icon="mdi-magnify"
      placeholder="Search events..."
      variant="outlined"
      hide-details
      class="mb-6"
      @keyup.enter="goToSearch"
    />

    <!-- Status Filter -->
    <v-chip-group class="mb-6">
      <v-chip
        v-for="opt in statusOptions"
        :key="opt.label"
        :color="activeStatus === opt.value ? 'primary' : undefined"
        :variant="activeStatus === opt.value ? 'flat' : 'outlined'"
        @click="setFilter(opt.value)"
      >
        {{ opt.label }}
      </v-chip>
    </v-chip-group>

    <!-- Loading State (idle = SSR placeholder when server fetch is skipped) -->
    <v-row v-if="status === 'pending' || status === 'idle'">
      <v-col v-for="n in 6" :key="n" cols="12" sm="6" md="4">
        <v-skeleton-loader type="card" />
      </v-col>
    </v-row>

    <!-- Error State -->
    <v-alert
      v-else-if="status === 'error'"
      type="error"
      variant="tonal"
      class="mb-6"
    >
      <div class="d-flex align-center justify-space-between">
        <span>Failed to load events. Please try again.</span>
        <v-btn variant="text" color="error" @click="refresh()"> Retry </v-btn>
      </div>
    </v-alert>

    <!-- Empty State -->
    <div v-else-if="!data?.data.length" class="text-center py-12">
      <v-icon size="64" color="medium-emphasis" class="mb-4">
        mdi-calendar-blank-outline
      </v-icon>
      <p class="text-h6 text-medium-emphasis">No events found</p>
      <p class="text-body-2 text-medium-emphasis">
        Try changing your filters or check back later.
      </p>
    </div>

    <!-- Event Grid -->
    <template v-else>
      <v-row>
        <v-col
          v-for="event in data.data"
          :key="event.id"
          cols="12"
          sm="6"
          md="4"
        >
          <EventsEventCard :event="event" />
        </v-col>
      </v-row>

      <!-- Pagination -->
      <div v-if="data.meta.last_page > 1" class="d-flex justify-center mt-8">
        <v-pagination
          :model-value="data.meta.current_page"
          :length="data.meta.last_page"
          @update:model-value="setPage"
        />
      </div>
    </template>

    <!-- Community ideas section (interest-gauging placeholders — not confirmed,
         joinable events). Kept as its own divider-separated block so ideas never
         dilute trust in the commitment-grade community grid above. -->
    <v-divider class="my-10" />

    <div class="mb-6">
      <h2 class="text-h5 mb-1">Community ideas</h2>
      <p class="text-body-2 text-medium-emphasis mb-0">
        Coming soon — community-floated ideas gauging interest. Nothing here is
        confirmed, ticketed, or joinable yet.
      </p>
    </div>

    <IdeasSection />

    <!-- More to discover section (informational third-party listings) -->
    <v-divider class="my-10" />

    <div class="d-flex flex-wrap align-center ga-3 mb-6">
      <div>
        <h2 class="text-h5 mb-1">More to discover</h2>
        <p class="text-body-2 text-medium-emphasis mb-0">
          Informational third-party listings — not joinable Arvee events.
        </p>
      </div>
      <v-spacer />
      <DiscoverSubmitEventCta variant="tonal" prepend-icon="mdi-plus-box" />
    </div>

    <DiscoverInformationalEventsSection />
  </div>
</template>
